import React from "react";
import { AlertOctagon, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Persistent red banner shown at the very top of every page while an admin
 * is impersonating another user. Reads impersonation metadata from AuthContext
 * (populated when POST /api/admin/users/{id}/impersonate succeeded). Clicking
 * "Exit view mode" restores the admin's original session.
 */
export const ImpersonationBanner = () => {
  const { impersonating, endImpersonation, user } = useAuth();
  if (!impersonating) return null;
  const targetName = user?.full_name || impersonating?.target?.full_name;
  const targetRole = user?.role || impersonating?.target?.role;

  const onExit = async () => {
    await endImpersonation();
    toast.success("Exited view mode — you are back as admin");
    // Send the admin back to their portal.
    window.location.replace("/admin/users");
  };

  return (
    <div
      className="w-full bg-rose-600 text-white flex items-center justify-between px-4 py-2 text-sm font-medium sticky top-0 z-[60] shadow"
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <AlertOctagon size={16} className="shrink-0" />
        <span className="truncate">
          🔴 Viewing as <strong>{targetName || "…"}</strong>
          <span className="opacity-80"> ({targetRole})</span>
          <span className="hidden sm:inline opacity-80"> · originally logged in as {impersonating.admin?.email}</span>
        </span>
      </div>
      <button
        onClick={onExit}
        className="inline-flex items-center gap-1.5 rounded-full bg-white text-rose-700 hover:bg-rose-50 px-3 py-1 text-xs font-bold uppercase tracking-widest shrink-0"
        data-testid="impersonation-exit-btn"
      >
        <LogOut size={12} /> Exit view mode
      </button>
    </div>
  );
};

export default ImpersonationBanner;
